<template>
  <vue-select
    ref="UiVueSelect"
    v-model="selectedOption"
    :options="innerOptions"
    :label="label"
    :clearable="clearable"
    :append-to-body="appendToBody"
    v-bind="$attrs"
    v-on="$listeners"
    class="custom-vue-select"
    :calculate-position="calculatePosition"
    :components="{ OpenIndicator }"
    @open="onOpen"
    @close="onClose"
    @search="onSearchQueryChange"
  >
    <template #list-footer>
      <li v-show="!noMoreOptions || loading" ref="load" class="loader">
        <v-progress-circular
          color="blue500"
          indeterminate
          :size="10"
          :width="2"
          class="d-flex mr-2"
        />
      </li>
    </template>
    <template #no-options>
      <span v-show="noMoreOptions && !loading">
        {{ $t('base.noElementsFound') }}
      </span>
    </template>
  </vue-select>
</template>

<script>
import { handleError } from '@/helpers/errors'
import { VIcon } from 'vuetify/lib'
import debounce from 'lodash/debounce'
import keys from 'lodash/keys'
const OPTIONS_LIST_CLASS = 'vs__dropdown-menu'

export default {
  name: 'UiVueSelect',
  props: {
    value: {
      type: [Object, String, Array, Number, Boolean],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'title',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    lockScroll: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    renderOpened: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: null,
    },
    searchFunction: {
      type: Function,
      default: () => [],
    },
    fetchOnFocus: {
      type: Boolean,
      default: false,
    },
    refetchOnFocus: {
      type: Boolean,
      default: false,
    },
    searchParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      observer: null,
      selectedOption: null,
      dropdownOpenedOnce: false,
      innerOptions: [],
      loading: false,
      noMoreOptions: false,
      searchFilters: {
        page: 1,
        limit: 10,
        query: '',
        last: 1,
      },
      params: null,
    }
  },
  computed: {
    OpenIndicator() {
      return {
        render: h =>
          h(
            VIcon,
            {
              staticClass: 'v-icon--custom',
              props: { size: 20, color: '#b9b9b9' },
            },
            ['mdi-chevron-down'],
          ),
      }
    },
  },
  watch: {
    value: {
      handler(value) {
        this.selectedOption = value
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.options.length) {
      this.innerOptions = this.options
      this.noMoreOptions = true
    } else {
      this.params = keys(this.searchParams).length ? this.searchParams : null
      this.observer = new IntersectionObserver(this.infiniteScroll)
      if (!this.fetchOnFocus && !this.refetchOnFocus) this.onSearchChange('')
    }
    if (this.renderOpened && !this.dropdownOpenedOnce) {
        this.dropdownOpenedOnce = true
        this.$nextTick(() => {
          setTimeout(() => {
            const toggle = this.$refs.UiVueSelect?.$el?.querySelector('.vs__dropdown-toggle')
            if (toggle) {
              toggle.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
              toggle.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }))
              toggle.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            }
          }, 50)
        })
      }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    async onOpen() {
      this.onFocus()
      if (!this.noMoreOptions) {
        await this.$nextTick()
        this.observer.observe(this.$refs.load)
      }
    },
    onClose() {
      if (this.observer) this.observer?.disconnect()
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      if (this.searchFilters.query) return
      if (isIntersecting) {
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        await this.onLoadMore()
        await this.$nextTick()
        ul.scrollTop = scrollTop + 20
      }
    },
    calculatePosition(dropdownList, component, { width, left }) {
      const toggle = component.$refs.toggle
      const scrollY = window.scrollY
      const viewportHeight = window.innerHeight
      function update() {
        const toggleRect = toggle.getBoundingClientRect()
        const dropdownHeight = dropdownList.offsetHeight
        const spaceBelow = viewportHeight - toggleRect.bottom
        const spaceAbove = toggleRect.top
        let newTop
        if (spaceBelow < dropdownHeight + 8 && spaceAbove > dropdownHeight) {
          newTop = `${scrollY + toggleRect.top - dropdownHeight}px`
        } else {
          newTop = `${scrollY + toggleRect.bottom}px`
        }
        dropdownList.style.top = newTop
        dropdownList.style.left = `${left}`
        dropdownList.style.width = `${width}`
        dropdownList.style.zIndex = 9999
      }
      update()
      const observer = new ResizeObserver(() => {
        update()
      })
      observer.observe(dropdownList)
      return () => {
        if (observer) observer.disconnect()
        dropdownList.removeAttribute('style')
      }
    },
    onFocus() {
      if (!this.filteredOptions?.length && this.fetchOnFocus) this.onSearchChange('')
      if (this.refetchOnFocus) this.onSearchChange('')
    },
    onSearchQueryChange: debounce(function (query) {
      this.onSearchChange(query)
    }, 300),
    async onSearchChange(query) {
      this.loading = true
      try {
        this.searchFilters.page = 1
        this.searchFilters.query = query
        this.noMoreOptions = false

        if (this.options.length) {
          const q = (query || '').toLowerCase()
          this.innerOptions = q
            ? this.options.filter(o => String(o[this.label] || '').toLowerCase().includes(q))
            : [...this.options]
          this.noMoreOptions = true
          this.loading = false
          return
        }

        const { limit, page } = this.searchFilters
        const result = await this.searchFunction({ query, limit, page }, this.params)
        this.innerOptions = result?.options
        const pagination = result?.pagination

        const listEl = this.$refs.UiVueSelect?.$el.getElementsByClassName(OPTIONS_LIST_CLASS)
        if (listEl?.length) {
          listEl[0].scrollTo(0, 0)
        }

        this.searchFilters.last = pagination?.maxPageNumber || 1
        this.noMoreOptions = page >= this.searchFilters.last
        if (
          this.chooseFirstOption &&
          (!this.selectedOption || typeof this.selectedOption !== 'object')
        ) {
          this.selectedOption = this.innerOptions.length ? this.innerOptions[0] : ''
          this.onInput()
        }
      } catch (err) {
        handleError(err)
      } finally {
        this.loading = false
      }
    },
    async onLoadMore() {
      if (this.loading) return
      this.loading = true
      try {
        this.searchFilters.page += 1
        const { query, page, limit } = this.searchFilters

        const { options } = await this.searchFunction({ query, page, limit }, this.params)
        this.innerOptions = [...this.innerOptions, ...options]
        this.noMoreOptions = page >= this.searchFilters.last
      } catch (err) {
        handleError(err)
      } finally {
        this.loading = false
      }
    },
    resetSearch(filter = null) {
      this.searchFilters = {
        page: 1,
        limit: 10,
        query: '',
        last: 1,
      }
      this.params = filter
      this.onSearchChange('')
    },
  },
}
</script>

<style lang="scss">
.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
}
.custom-vue-select {
  &:not(.vs--open) {
    .vs__search {
      padding: 0;
    }
  }
  .vs__search {
    font-size: 12px;
  }
  .vs__dropdown-toggle {
    background-color: transparent;
    border: none;
    padding-left: 7px;
    padding-top: 1px;
    .vs__selected-options {
      flex-wrap: nowrap;
      white-space: nowrap;
      .vs__selected {
        max-width: 71px;
        overflow: hidden;
      }
    }
    .vs__actions {
      padding-right: 10px;
      padding-top: 4px;
      padding-left: 0;
    }
  }
}
.vs--single.vs--open .vs__selected {
  opacity: 1;
}
.vs__dropdown-menu {
  font-family: 'Roboto Flex', sans-serif;
  padding: 4px 0 0;
  border: none;
  .vs__dropdown-option {
    display: flex;
    align-items: center;
    font-size: 12px;
    padding: 0 12px;
    min-height: 40px;
    line-height: 16px;
    text-transform: none;
    vertical-align: middle;
    text-wrap: auto;
  }
  .vs__no-options {
    font-size: 12px;
    padding: 10px 0;
  }
}
</style>
